// Formatação do indicador de Lead Time de Fatura (PO1-3000 / PO1-3001).
//
// O contrato dos endpoints novos é sempre em MINUTOS (mediaMin, medianaMin,
// p90Min, maxMin, janelaMin). Quem traduz para a unidade legível é a tela: a
// mesma resposta serve para "86 min" e para "2,9 d" sem o backend ter que
// adivinhar a escala.
//
// Funções puras, sem React e sem moment: testadas em __tests__/formatos.test.js.
// Node 12 no build (Amplify): sem `??` e sem optional chaining.

const MESES = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];

// Até 1 casa decimal, com vírgula e sem zero à direita: 86 -> "86",
// 8,833 -> "8,8", 2 -> "2".
//
// Sem `toLocaleString("pt-BR")` de propósito: o Node 12 do build e do Jest vem
// com small-icu (só en-US), então o separador viraria ponto fora do navegador e
// o teste passaria a depender do ICU da máquina.
const dec1 = (n) => String(Math.round(Number(n) * 10) / 10).replace(".", ",");

/**
 * Minutos -> texto legível.
 *   < 120 min  -> "86 min"   (a janela da auditoria boa é de minutos)
 *   < 48 h     -> "8,8 h"
 *   senão      -> "2,9 d"
 * Sem valor (null/undefined/NaN) devolve "-": ausência de medição não vira 0.
 */
export const fmtMinutos = (minutos) => {
  if (minutos === null || minutos === undefined || minutos === "") return "-";
  const n = Number(minutos);
  if (!Number.isFinite(n)) return "-";
  // Duração negativa é dado inconsistente (transição carimbada antes da
  // anterior), não um tempo curto: cai em "-" como o `diffDias` da H1, em vez
  // de virar um "-47 min" que ninguém sabe ler.
  if (n < 0) return "-";

  if (n < 120) return `${dec1(n)} min`;

  const horas = n / 60;
  if (horas < 48) return `${dec1(horas)} h`;

  return `${dec1(horas / 24)} d`;
};

// "2026-08-31" -> { ano: 2026, mes: 8, dia: 31 }. Parse manual de propósito:
// `new Date("2026-08-31")` é meia-noite UTC e, no fuso do Brasil, voltaria um
// dia na hora de formatar.
const partesData = (periodo) => {
  if (!periodo) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(periodo));
  if (!m) return null;
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return null;
  return { ano: Number(m[1]), mes, dia: Number(m[3]) };
};

/**
 * Rótulo completo do bucket da série de volume, por granularidade.
 *   dia    -> "31/08/2026"
 *   semana -> "Semana de 31/08/2026"
 *   mes    -> "ago/2026"
 * O backend devolve sempre o INÍCIO do bucket em ISO (YYYY-MM-DD).
 */
export const fmtPeriodo = (periodo, granularidade) => {
  const p = partesData(periodo);
  if (!p) return "-";

  const dd = String(p.dia).padStart(2, "0");
  const mm = String(p.mes).padStart(2, "0");

  if (granularidade === "mes") return `${MESES[p.mes - 1]}/${p.ano}`;
  if (granularidade === "semana") return `Semana de ${dd}/${mm}/${p.ano}`;
  return `${dd}/${mm}/${p.ano}`;
};

/**
 * Versão curta para o eixo X do gráfico, onde 60 rótulos completos não cabem.
 *   dia/semana -> "31/08"
 *   mes        -> "ago/26"
 */
export const fmtPeriodoCurto = (periodo, granularidade) => {
  const p = partesData(periodo);
  if (!p) return "-";

  if (granularidade === "mes") {
    return `${MESES[p.mes - 1]}/${String(p.ano).slice(2)}`;
  }
  return `${String(p.dia).padStart(2, "0")}/${String(p.mes).padStart(2, "0")}`;
};

export const GRANULARIDADES = [
  { key: "dia", label: "Dia" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mês" },
];

// Valor em reais SEM centavos. O cartão "Valor integrado" mostra milhões, e
// os centavos só empurram o número para fora da largura do cartão.
// Formatação manual, e não Intl: o Node 12 do build e do Jest é small-icu e
// devolveria ponto no lugar do separador de milhar (mesmo motivo de fmtMinutos).
export const fmtBRLInteiro = (v) => {
  const n = Math.round(Number(v) || 0);
  const digitos = Math.abs(n).toString();
  let corpo = "";
  for (let i = 0; i < digitos.length; i += 1) {
    if (i > 0 && (digitos.length - i) % 3 === 0) corpo += ".";
    corpo += digitos[i];
  }
  return `${n < 0 ? "-" : ""}R$ ${corpo}`;
};
